import moment from 'moment/moment';
import React, { SetStateAction, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Campaign } from '../../../../../../../model/campaign/Campaign';
import { IOfferItems } from '../../../../../../../model/offer/IOfferItems';
import { User } from '../../../../../../../model/user/User';
import { CustomAutocomplete } from '../../../../../../../shared/components/Autocomplete';
import CustomDatePicker from '../../../../../../../shared/components/DatePicker';
import {
    formatDate,
    getLocalDateWithoutTimezone,
    isSelectedDateValid,
    removeTimeZoneFromDate,
} from '../../../../../../../shared/functions/Functions';
import { manageBrand } from '../../../../../../../store/brand/manageBrandSlice';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { ErrorToast } from '../../../../../../../utils/toasters';
import { ManageService } from '../../../service';
import { SelectedUserCard } from '../SelectedUserCard';
import { useTranslation } from 'react-i18next';

export interface iCalendarModalProps {
    show: boolean;
    closeModal: () => void;
    onSend: () => void;
    state: any;
    setState: React.Dispatch<SetStateAction<any>>;
    setSelectedEventId?: React.Dispatch<SetStateAction<number>>;
    campaignId?: number;
    triggerUpdate?: boolean;
    campaign?: Campaign;
}

export const CalendarCreateModal = ({
    show,
    closeModal,
    campaign,
    state,
    setState,
    onSend,
    campaignId,
    triggerUpdate,
}: iCalendarModalProps) => {
    const dispatch = useDispatch();
    const { selectedInfluencer: selectedCardInfluencer } = useSelector((state: IAllStates) => state.manageBrand);
    const [isFieldDisabled, setIsFieldDisabled] = useState(false);
    const [filterInfluencers, setFilterInfluencers] = useState({
        page: 1,
        perPage: 10,
        userId: '',
        displayName: '',
    });

    const [listOfAllInfluencers, setListOfAllInfluencers] = useState<User[]>([]);
    const [selectedInfluencer, setSelectedInfluencer] = useState<User>();
    const { t } = useTranslation();

    const handleChangeTimepicker = (newValue: Date | null) => {
        const value = getLocalDateWithoutTimezone(newValue as Date);
        setState((prev: { postingDate: string }) => ({
            ...prev,
            postingDate: value,
        }));
    };

    const handleChange = (paramKey: string, paramValue: number | string | string[]) => {
        if (paramKey === 'offerId') {
            const selectedUser = listOfAllInfluencers?.find(user => user?.offerId === Number(paramValue));
            setSelectedInfluencer(selectedUser);
            setState((prev: object) => ({
                ...prev,
                userId: selectedUser?.id,
            }));
        } else {
            if (paramKey === 'userId') {
                const selectedUser = listOfAllInfluencers?.find(user => user?.id === Number(paramValue));
                setSelectedInfluencer(selectedUser);
            }
            setState((prev: object) => ({
                ...prev,
                [paramKey]: +paramValue,
            }));
        }
    };

    useEffect(() => {
        setIsFieldDisabled(!!state?.userId);
    }, [JSON.stringify(state)]);

    useEffect(() => {
        campaignId &&
            ManageService.getFilterInfluencers(+campaignId, filterInfluencers)
                .then(res => {
                    setListOfAllInfluencers(
                        res.data?.map((user: User) => ({ ...user, offerId: user?.offers?.[0]?.id }))
                    );
                    if (isFieldDisabled) {
                        setSelectedInfluencer(res.data?.find((influencer: User) => influencer?.id === state?.userId));
                    }
                })
                .catch(error => ErrorToast(error));
    }, [JSON.stringify(filterInfluencers), campaignId, triggerUpdate]);

    const allPossiblePlatforms = selectedInfluencer?.offers?.[0]?.offerItems?.sort((a, b) =>
        b.socialPlatform.name > a.socialPlatform.name ? 1 : -1
    );
    const selectedPlatform = allPossiblePlatforms?.find(platform => platform.id === state?.offerItemId);
    // The contract has an amendment waiting for a signature, its postings cannot be scheduled until it is signed
    const hasOpenAnnex = !!selectedInfluencer?.offers?.[0]?.hasOpenAnnex;

    useEffect(() => {
        if (
            selectedCardInfluencer &&
            show &&
            listOfAllInfluencers?.filter((influencer: any) =>
                influencer?.offers?.[0]?.offerItems?.some(
                    (offer: IOfferItems) => offer.nbPosts > (offer?.eventNbPosts ?? 0)
                )
            )?.length === 1
        ) {
            setState((prev: object) => ({ ...prev, userId: listOfAllInfluencers?.[0]?.id }));
        }
    }, [JSON.stringify(listOfAllInfluencers), show]);

    useEffect(() => {
        if (selectedCardInfluencer?.user?.id) {
            handleChange('userId', Number(selectedCardInfluencer?.user?.id));
            dispatch(manageBrand.actions.setSelectedInfluencer(undefined));
        }
    }, [selectedCardInfluencer?.user?.id]);

    useEffect(() => {
        if (isFieldDisabled && !selectedInfluencer?.id) {
            setSelectedInfluencer(listOfAllInfluencers?.find(influencer => influencer?.id === state?.userId));
        }
    }, [isFieldDisabled, selectedInfluencer?.id]);

    const getFeedbackLoopDate = (isReviewed = false, reviewDays = 1, postingDate: string | undefined) => {
        if (isReviewed) {
            // if campaign is reviewed substcract reviews days
            return moment(postingDate).subtract(reviewDays, 'days').toISOString();
        }
        return postingDate; // use selected calendar date
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            className="height-95vh"
        >
            <Modal.Header>
                <Modal.Title className="mt-3 w-100">
                    <h4 className="border-bottom m-0 pb-2">{`${t('brand.campaign.manage.modal.schedulePosting')}`}</h4>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {hasOpenAnnex && (
                    <div className="annex-description-card mb-3">
                        <h6 className="mb-1">{t('brand.campaign.book.contract.modal.annex.spending_blocked_title')}</h6>
                        <p className="mb-0">{t('brand.campaign.book.contract.modal.annex.schedule_blocked')}</p>
                    </div>
                )}
                <div className="row">
                    <div className="col-12 my-2">
                        {!isFieldDisabled ? (
                            <CustomAutocomplete
                                inputLabelTranslation={t('brand.campaign.manage.influencerName')}
                                isWithImage={true}
                                setFilterState={setFilterInfluencers}
                                changeEventKey="displayName"
                                handleChangeParams={handleChange}
                                dataArray={listOfAllInfluencers?.filter((influencer: any) =>
                                    influencer?.offers?.[0]?.offerItems?.some(
                                        (offer: IOfferItems) => offer.nbPosts > (offer?.eventNbPosts ?? 0)
                                    )
                                )}
                                inputLabel="displayName"
                                photoLabel="profilePhoto.path"
                                queryFilterKey="offerId"
                                selectedStateImage={selectedInfluencer?.profilePhoto?.path as string}
                                me={true}
                            />
                        ) : (
                            <SelectedUserCard
                                profilePhoto={selectedInfluencer?.profilePhoto?.path as string}
                                totalPosts={selectedInfluencer?.offers?.[0]?.scheduledPosts?.totalNbPosts as number}
                                isLongTerm={selectedInfluencer?.offers?.[0]?.isLongTerm}
                                isAddonsOnly={selectedInfluencer?.offers?.[0]?.isAddonsOnly}
                                totalScheduledPosts={selectedInfluencer?.offers?.[0]?.scheduledPosts?.created as number}
                                displayName={
                                    selectedInfluencer?.displayName?.trim() ||
                                    `${selectedInfluencer?.firstName ?? ''} ${
                                        selectedInfluencer?.lastName ?? ''
                                    }`.trim()
                                }
                                closeHandler={() => {
                                    setFilterInfluencers(prev => ({
                                        ...prev,
                                        displayName: '',
                                    }));
                                    setState((prev: any) => ({ postingDate: prev.postingDate }));
                                }}
                            />
                        )}
                    </div>
                    {isFieldDisabled && (
                        <>
                            <div className="col-12 my-2">
                                <CustomAutocomplete
                                    dataArray={allPossiblePlatforms as IOfferItems[]}
                                    isWithImage={true}
                                    inputLabel="socialPlatform.name"
                                    photoLabel="socialPlatform.path"
                                    selectedStateImage={selectedPlatform?.socialPlatform?.path as string}
                                    queryFilterKey="offerItemId"
                                    inputLabelTranslation={`${t('brand.campaign.manage.postingType')}`}
                                    handleChangeParams={handleChange}
                                />
                            </div>
                            <div className="col-12 my-2">
                                <CustomDatePicker
                                    label={t('brand.campaign.manage.postingDate')}
                                    disableFrom={campaign?.publishFrom ?? ''}
                                    disableTo={removeTimeZoneFromDate(campaign?.publishTo ?? '')}
                                    value={state?.postingDate}
                                    onChange={handleChangeTimepicker}
                                />
                                {!isSelectedDateValid(
                                    state?.postingDate,
                                    campaign?.publishFrom,
                                    campaign?.publishTo
                                ) && (
                                    <p className="text-danger">
                                        {formatDate(state?.postingDate)} {t('influencer.planner.notInRange')}
                                        {formatDate(campaign?.publishFrom as string)} -{' '}
                                        {formatDate(campaign?.publishTo as string)}
                                    </p>
                                )}
                            </div>

                            <div className="col-12 my-2">
                                <CustomDatePicker
                                    label={t('brand.campaign.manage.feedBackLoopEnddate')}
                                    disabled
                                    value={getFeedbackLoopDate(
                                        campaign?.isReviewed,
                                        campaign?.reviewDays,
                                        state?.postingDate
                                    )}
                                    onChange={handleChangeTimepicker}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" className="btn-sm" onClick={closeModal}>
                    {t('general.button.close')}
                </Button>
                <Button
                    variant="primary"
                    className="btn-sm"
                    onClick={onSend}
                    disabled={
                        hasOpenAnnex ||
                        !isSelectedDateValid(state?.postingDate, campaign?.publishFrom, campaign?.publishTo)
                    }
                >
                    {t('general.button.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
